// Angular
import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

// Afidium
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { IProperty } from 'libraries/service/resources/hotel/ISearchResponse';
import { wenji } from 'libraries/interfaces/IWenji';
import { MenuDetailsService } from 'libraries/hotel/components/menu-details/services/menu-details.service';
import { IMenuDetails } from 'libraries/hotel/components/menu-details/menu-details.component';
import { IMapConfiguration } from 'libraries/interfaces/IMapConfiguration';
import { IMarkerPoint, MarkerColor, MarkerSize } from 'libraries/map/IPoint';
import { IConfigSharedHotel } from 'libraries/hotel/models/config-shared-hotel';
import { ConfigSharedHotelService } from 'libraries/service/resources/configuration/config-shared-hotel.service';
import { ErrorTracingService } from 'libraries/service/error-tracing/error-tracing.service';
import { IAlternative } from 'libraries/hotel/models/hotel';
import { HotelInformationsModalService } from 'libraries/hotel/components/hotel-informations-modal/service/hotel-informations-modal.service';
import { IHotelProperty } from 'libraries/service/resources/model/search/hotel/IHotelProperty';

//libs
import { faExclamationTriangle, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { LanguageService } from "libraries/service/language/language.service";

export interface IHotelInformations {
    property: IProperty;
    mealplan?: {
        name: string;
        code: string;
    };
}

export interface IPropertyDescriptionModal {
    hotelInformation: IHotelInformations;
    bookingHost: string;
}

@Component({
    selector: 'property-description',
    template: `
        <card>
            <div class="card-header-content" mat-dialog-title>
                <hotel-resume
                        class="accommodation"
                        [hotel]="hotelInformations.property"
                        [mealPlan]="hotelInformations.mealplan">
                </hotel-resume>
                <btn type="actionDo"
                     [label]="labels.closeDialog"
                     (click)="closeDialog()">
                </btn>
            </div>

            <div class="card-body-content" mat-dialog-content>
                <notification *ngIf="hotelInformations.property && hasDifferentLanguage()"
                              type="warning">
                    <fa-icon [icon]="iconExclamationTriangle"></fa-icon> {{ wording.languagesWarning }}
                </notification>

                <div class="menu-container">
                    <menu-details [items]="menuItems" (onChangeTab)="changeTab($event)"></menu-details>

                    <hotel-informations-alternative-select
                            [isActive]="true"
                            *ngIf="hotelInformations.property && haveConfigInformations"
                            [currentProperty]="hotelInformations.property"
                            [propertyAlternatives]="currentAlternatives"
                            [isHostOrGiataChoice]="true"
                            [isWithGiata]="isWithGiata"
                            [isPermModalAlternative]="isPermModalAlternative"
                            (currentPropertyChange)="changeSelectedHotelProperty($event)">
                    </hotel-informations-alternative-select>
                </div>

                <description *ngIf="tab === 'details'" [description]="hotelInformations.property"></description>
                <informations *ngIf="tab === 'contacts'" [informations]="hotelInformations.property">
                    <map *ngIf="markers && markers.length"
                         [configuration]="mapConfiguration"
                         [points]="markers"
                         [defaultZoom]="17"></map>
                </informations>
            </div>
        </card>
    `,
    styleUrls: ['./property-description.component.scss'],
})
export class PropertyDescriptionComponent implements OnInit {
    public hotelInformations: IHotelInformations;
    public currentAlternatives: IAlternative[];
    public bookingHost: string;

    public isWithGiata: boolean = false;
    public isPermModalAlternative: boolean = true;
    public haveConfigInformations: boolean = false;

    public tab: string = 'details';
    public labels: ILabels = {
        closeDialog: Yii.t('wui-shared-js', 'Fermer')
    };

    public tabLabels: { [key: string]: string } = {
        details: Yii.t('wui-shared-js', 'Détails'),
        map: Yii.t('wui-shared-js', 'Contacts'),
    };

    public readonly wording: ILabels = {
        languagesWarning: Yii.t("wui-shared-js", "Une ou plusieurs descriptions ne sont pas disponibles dans la langue de votre environnement"),
    };

    public menuItems: IMenuDetails[] = [
        {
            code: 'details',
            name: this.tabLabels.details,
            exist: true,
            selected: true
        },
        {
            code: 'contacts',
            name: this.tabLabels.map,
            exist: true,
            selected: false
        }
    ];

    public label: ILabels = {
        loaderMessage: Yii.t('wui-shared-js', 'Chargement des informations')
    };
    public mapConfiguration: IMapConfiguration = wenji.ui.map;
    public markers: IMarkerPoint[];

    public iconExclamationTriangle: IconDefinition = faExclamationTriangle;

    private tempProperty: IProperty;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IPropertyDescriptionModal,
        public dialog: MatDialog,
        private dialogRef: MatDialogRef<PropertyDescriptionComponent>,
        private readonly menuDetailsService: MenuDetailsService,
        private readonly configSharedHotelService: ConfigSharedHotelService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly hotelInformationsModalService: HotelInformationsModalService,
        private readonly languageService: LanguageService
    ) {
    }

    public ngOnInit(): void {
        this.getConfig();
        this.hotelInformations = this.data.hotelInformation;
        this.currentAlternatives = this.data.hotelInformation.property.alternatives;
        this.bookingHost = this.data.bookingHost;
        this.generateMarkers();
    }

    public changeTab(tabCode: string): void {
        this.menuDetailsService.changeTab(this.menuItems, tabCode);
        this.tab = tabCode;
    }

    public toNumber(numberString: string): number {
        return Number(numberString);
    }

    public closeDialog(): void {
        this.dialogRef.close(this.tempProperty);
    }

    public changeSelectedHotelProperty(property: IProperty): void {
        this.tempProperty = property;
        this.hotelInformations.property = this.hotelInformationsModalService.changeHotelPropertyAfterGetProperty(
            this.hotelInformations.property,
            property,
        );
    }

    public hasDifferentLanguage(): boolean {
        return this.hotelInformationsModalService.hasDifferentLanguage(this.hotelInformations.property as unknown as IHotelProperty, this.languageService.userLanguage);
    }

    private generateMarkers(): void {
        if (this.hotelInformations.property.geoposition) {
            this.markers = [{
                color: MarkerColor.Red,
                latitude: this.hotelInformations.property.geoposition.latitude,
                longitude: this.hotelInformations.property.geoposition.longitude,
                size: MarkerSize.Default,
            }];
        }

    }

    private getConfig(): void {
        this.configSharedHotelService.getConfiguration().subscribe({
            next: (sharedHotelConfig: IConfigSharedHotel): void => {
                this.haveConfigInformations = true;
                this.isWithGiata = sharedHotelConfig.detail.property.isWithGiata;
                this.isPermModalAlternative = sharedHotelConfig.detail.property.isPermModalAlternative;
            },
            error: (error: Error): void => {
                this.errorTracingService.captureException(error);
            }
        });
    }
}
